import React from "react";

import {
  Box,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  MenuProps,
  Tooltip,
  alpha,
  styled,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EditIcon from "@mui/icons-material/Edit";

import { RoleName } from "../../../../common/enums/RoleName";
import RoleBasedAccess from "../../../../common/RoleBasedAccess";

const StyledMenu = styled((props: MenuProps) => (
  <Menu
    elevation={0}
    anchorOrigin={{
      vertical: "bottom",
      horizontal: "right",
    }}
    transformOrigin={{
      vertical: "top",
      horizontal: "right",
    }}
    {...props}
  />
))(({ theme }) => ({
  "& .MuiPaper-root": {
    borderRadius: 4,
    marginTop: theme.spacing(1),
    minWidth: 180,
    color:
      theme.palette.mode === "light"
        ? "rgb(55, 65, 81)"
        : theme.palette.grey[300],
    boxShadow: `rgb(255, 255, 255) 0px 0px 0px 0px, 
    rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, 
    rgba(0, 0, 0, 0.1) 0px 2px 4px -3px, 
    rgba(0, 0, 0, 0.05) 0px 2px 4px -2px`,
    "& .MuiMenu-list": {
      padding: "2px 0",
    },
    "& .MuiMenuItem-root": {
      "& .MuiSvgIcon-root": {
        fontSize: 18,
        color: theme.palette.text.secondary,
        marginRight: theme.spacing(1.5),
      },
      "&:active": {
        backgroundColor: alpha(
          theme.palette.primary.main,
          theme.palette.action.selectedOpacity,
        ),
      },
    },
  },
}));

interface Props {
  handleOpenInsightsModalManager: () => void;
  handleCopy: () => void;
}

export default function ActionsList({
  handleOpenInsightsModalManager,
  handleCopy,
}: Props) {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const onCopy = () => {
    handleCopy();
    handleClose();
  };

  return (
    <Box sx={{ float: "right", flexShrink: 0 }}>
      <Tooltip title="Actions">
        <IconButton
          size="small"
          color="inherit"
          sx={{
            color: "text.secondary",
            border: "1px solid",
            borderColor: "divider",
            width: 32,
            height: 32,
            "&:hover": {
              borderColor: "primary.main",
              color: "primary.main",
              backgroundColor: "action.hover",
            },
          }}
          id="insights-actions-button"
          aria-controls={open ? "basic-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={open ? "true" : undefined}
          onClick={handleClick}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <StyledMenu
        id="composition-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "insights-actions-button",
        }}
      >
        <MenuItem onClick={onCopy}>
          <ContentCopyIcon />
          Copy
        </MenuItem>
        <RoleBasedAccess allowedRoles={[RoleName.ADMIN, RoleName.EDITOR]}>
          <>
            <Divider />
            <MenuItem onClick={handleOpenInsightsModalManager}>
              <EditIcon />
              Edit
            </MenuItem>
          </>
        </RoleBasedAccess>
      </StyledMenu>
    </Box>
  );
}
